// src/pages/Auth/index.jsx
import React, { useState } from 'react'; 
import { Form, Input, Button, Card, Typography, message, Tabs } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../utils/userContext.js';
import './index.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const USER_STORAGE_KEY = "blog_users";

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login"); 
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUser();

  const getUsers = () => {
    const usersStr = localStorage.getItem(USER_STORAGE_KEY);
    return usersStr ? JSON.parse(usersStr) : [];
  };

  const addUser = (userData) => {
    const users = getUsers();
    const userExist = users.find(u => u.username === userData.username);
    const emailExist = users.find(u => u.email === userData.email);
    if (userExist) throw new Error("用户名已存在");
    if (emailExist) throw new Error("邮箱已被注册");

    const newUser = {
      id: Date.now(),
      username: userData.username,
      password: userData.password,
      email: userData.email
    };
    users.push(newUser);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(users));
    return newUser;
  };

  const validateUser = (identifier, password) => {
    const users = getUsers();
    const user = users.find(u => u.username === identifier || u.email === identifier);
    if (!user) throw new Error("用户不存在");
    if (user.password !== password) throw new Error("密码错误");
    return user;
  };

  const onFinishLogin = (values) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      try {
        const user = validateUser(values.identifier, values.password);
        message.success("登录成功！");
        login({ 
          id: user.id,
          username: user.username,
          email: user.email
        });
        const from = location.state?.from?.pathname || "/";
        navigate(from);
      } catch (error) {
        message.error(error.message || "登录失败！");
      }
    }, 500);
  };

  const onFinishRegister = (values) => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      try {
        if (values.password !== values.confirmPassword) {
          message.error("两次密码不一致！");
          return;
        }
        if (values.password.length < 6 || values.username.length < 2) {
          message.error("用户名至少2位，密码至少6位！");
          return;
        }
        addUser({
          username: values.username,
          password: values.password,
          email: values.email
        });
        message.success("注册成功！请登录");
        setActiveTab("login"); 
      } catch (error) {
        message.error(error.message || "注册失败！");
      }
    }, 500);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-header">
            <Title level={2} className="auth-title">我的博客</Title>
            <Text className="auth-subtitle">欢迎登录/注册</Text>
          </div>

          <Tabs activeKey={activeTab} onChange={handleTabChange} centered>
            <TabPane tab="用户登录" key="login">
              <Form
                name="login"
                initialValues={{ identifier: "", password: "" }}
                onFinish={onFinishLogin}
                autoComplete="off"
              >
                <Form.Item
                  name="identifier"
                  label="用户名/邮箱"
                  rules={[{ required: true, message: "请输入用户名或邮箱！" }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="用户名或邮箱" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="密码"
                  rules={[{ required: true, message: "请输入密码！" }]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    {loading ? '登录中...' : '登录'}
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>

            <TabPane tab="用户注册" key="register">
              <Form
                name="register"
                onFinish={onFinishRegister}
                autoComplete="off"
              >
                <Form.Item
                  name="username"
                  label="用户名"
                  rules={[{ required: true, message: "请输入用户名！" }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="请输入用户名" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="邮箱"
                  rules={[{ required: true, message: "请输入邮箱！" }, { type: "email", message: "请输入有效邮箱！" }]}
                >
                  <Input prefix={<MailOutlined />} placeholder="请输入邮箱" />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="密码"
                  rules={[{ required: true, message: "请输入密码！" }]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="请输入密码" />
                </Form.Item>

                <Form.Item
                  name="confirmPassword"
                  label="确认密码"
                  rules={[{ required: true, message: "请确认密码！" }]}
                >
                  <Input.Password prefix={<LockOutlined />} placeholder="请再次输入密码" />
                </Form.Item>

                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={loading} block>
                    {loading ? '注册中...' : '注册'}
                  </Button>
                </Form.Item>
              </Form>
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default Auth;